import { FC, useEffect } from 'react';
import { Cloud } from 'lucide-react';
import { trpcClient } from '@/lib/trpc';
import type { CloudProvider } from '@deepguide-ai/types';
import type { ConfigModalProps, IntegrationFeature, IntegrationProvider } from '../types';
import {
  CloudConnectionManagerDialog,
  useScreenataCloudConnectionManager,
} from '@/components/screenata/cloud-connections';

type CloudProviderId = CloudProvider;

interface CloudProviderDefinition {
  integrationType: 'AWS_READONLY' | 'GCP_READONLY' | 'AZURE_READONLY' | 'Cloudflare' | 'Aliyun' | 'TencentCloud';
  providerId: CloudProviderId;
  name: string;
  description: string;
}

const PROVIDERS: Record<CloudProviderId, CloudProviderDefinition> = {
  aws: {
    integrationType: 'AWS_READONLY',
    providerId: 'aws',
    name: 'AWS',
    description: 'Run deterministic IAM, S3 encryption, CloudTrail, and password policy checks.',
  },
  gcp: {
    integrationType: 'GCP_READONLY',
    providerId: 'gcp',
    name: 'Google Cloud',
    description: 'Run deterministic IAM, storage encryption, audit logs, and firewall checks.',
  },
  azure: {
    integrationType: 'AZURE_READONLY',
    providerId: 'azure',
    name: 'Azure',
    description: 'Run deterministic RBAC, storage encryption, activity log, and NSG checks.',
  },
  tencent: { integrationType: 'TencentCloud', providerId: 'tencent', name: 'Tencent Cloud',
    description: 'Discover CAM access, CVM instances and CloudAudit configuration.' },
  aliyun: { integrationType: 'Aliyun', providerId: 'aliyun', name: 'Alibaba Cloud (Aliyun)',
    description: 'Discover RAM access, ECS instances and ActionTrail configuration.' },
  cloudflare: {
    integrationType: 'Cloudflare',
    providerId: 'cloudflare',
    name: 'Cloudflare',
    description: 'Run deterministic account, zone, WAF, and R2 storage checks.',
  },
};

const CloudIconWrapper: FC = () => <Cloud className="h-5 w-5" />;

function CloudConfigModal({ provider, open, onOpenChange, onComplete }: ConfigModalProps & { provider: CloudProviderId }) {
  const cloudManager = useScreenataCloudConnectionManager({
    showConnectResult: provider === 'cloudflare' || provider === 'aliyun' || provider === 'tencent',
    onConnected: () => {
      if (provider === 'cloudflare' || provider === 'aliyun' || provider === 'tencent') return;
      onOpenChange(false);
      onComplete();
    },
  });
  const { setConfiguringProviderId } = cloudManager;
  useEffect(() => {
    setConfiguringProviderId(open ? provider : null);
  }, [open, provider, setConfiguringProviderId]);

  return (
    <CloudConnectionManagerDialog
      cloudManager={cloudManager}
      onClose={() => onOpenChange(false)}
    />
  );
}

function createCloudProvider(provider: CloudProviderId): IntegrationProvider {
  const definition = PROVIDERS[provider];

  const ConfigModalForProvider: FC<ConfigModalProps> = (props) => {
    return <CloudConfigModal {...props} provider={provider} />;
  };

  return {
    id: definition.integrationType,
    name: definition.name,
    description: definition.description,
    Icon: CloudIconWrapper,
    ConfigModal: ConfigModalForProvider,
    handleConnect: async ({ onSuccess }) => {
      onSuccess?.({ provider });
      return { success: true };
    },
    onConnectComplete:
      provider === 'aws' || provider === 'cloudflare' || provider === 'aliyun' || provider === 'tencent'
        ? undefined
        : async ({ code, state }) => {
            return trpcClient.integrations.completeCloudAuth.mutate({
              provider,
              code,
              state,
            });
          },
    completePendingConnect:
      provider === 'aws' || provider === 'cloudflare' || provider === 'aliyun' || provider === 'tencent'
        ? undefined
        : async ({ selectionToken, subscriptionId, projectId }) => {
            return trpcClient.integrations.finalizeCloudAuthSelection.mutate({
              provider,
              selectionToken,
              ...(subscriptionId ? { subscriptionId } : {}),
              ...(projectId ? { projectId } : {}),
            });
          },
    getFeatures: (): IntegrationFeature[] => [
      {
        id: 'cloud_scan',
        name: 'Cloud scan',
        description: 'Run deterministic infrastructure compliance checks',
        enabled: true,
      },
    ],
    syncData: async () => {
      return;
    },
  };
}

export const AwsReadonlyProvider = createCloudProvider('aws');
export const GcpReadonlyProvider = createCloudProvider('gcp');
export const AzureReadonlyProvider = createCloudProvider('azure');
export const TencentProvider = createCloudProvider('tencent');
export const AliyunProvider = createCloudProvider('aliyun');
export const CloudflareProvider = createCloudProvider('cloudflare');
